import type { ReactNode, TableHTMLAttributes } from "react";

import { cn } from "../lib/cn.js";

/**
 * A data table.
 *
 * A compound component — `Table`, `Table.Head`, `Table.Body`, `Table.Row`, `Table.Cell`,
 * `Table.HeaderCell` — rather than a `columns`/`rows` prop API. The financial modules that will use
 * this need cells containing formatted money, links, and controls, and a data-driven API ends up
 * re-inventing JSX in a `render` prop to allow it.
 *
 * The wrapper scrolls horizontally on its own. A wide table inside a narrow column otherwise
 * widens the page and produces a horizontal scrollbar on the whole document, which is a far worse
 * experience than scrolling the table.
 */

function TableRoot({ className, ...rest }: TableHTMLAttributes<HTMLTableElement>): ReactNode {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full border-collapse text-sm", className)} {...rest} />
    </div>
  );
}

function Head({ children }: { children: ReactNode }): ReactNode {
  return <thead className="border-b border-input text-muted">{children}</thead>;
}

function Body({ children }: { children: ReactNode }): ReactNode {
  return <tbody>{children}</tbody>;
}

function Row({ children }: { children: ReactNode }): ReactNode {
  return <tr className="border-b border-input last:border-b-0">{children}</tr>;
}

/**
 * How a column reports and changes its own sort — the whole of this table's sorting API.
 *
 * PASSED IN RATHER THAN OWNED HERE, because sorting is a fact about a LIST and not about a cell. The
 * table has no idea what its rows are; only the consumer knows that an interval column orders by
 * duration rather than alphabetically, or that a money column holds an unnormalised figure. What the
 * primitive owns is the affordance and the accessibility — a real button, a state glyph, and
 * `aria-sort` on the `<th>` where a screen reader looks for it.
 */
export interface HeaderSort {
  /** `null` means "sortable, and not currently the sort" — which is not the same as not sortable. */
  direction: "asc" | "desc" | null;
  onToggle: () => void;
}

function HeaderCell({
  children,
  numeric = false,
  sort,
}: {
  children: ReactNode;
  /** Right-aligns, for money and counts. Alignment is the reader's cue that a column is arithmetic. */
  numeric?: boolean;
  /**
   * Makes this column sortable. Omit and the header is the plain `<th>` it has always been.
   *
   * PRESENCE IS THE FLAG, rather than a separate `sortable` boolean beside a nullable direction —
   * two props that can disagree, where a column marked sortable with no handler is a header that
   * looks clickable and does nothing.
   */
  sort?: HeaderSort;
}): ReactNode {
  const alignment = numeric ? "text-right" : "text-left";

  if (sort === undefined) {
    return (
      <th scope="col" className={cn("px-3 py-2 font-medium", alignment)}>
        {children}
      </th>
    );
  }

  return (
    <th
      scope="col"
      /*
       * `aria-sort` ON THE `<th>`, WHICH IS THE ONLY PLACE IT WORKS.
       *
       * It is defined for a column header, so on the button inside it is ignored — and the mistake
       * renders identically, which is why this is stated rather than left to be read off the JSX.
       * "none" for a sortable column that is not the current sort: omitting the attribute says the
       * column cannot be sorted at all, which is a different claim.
       */
      aria-sort={
        sort.direction === "asc" ? "ascending" : sort.direction === "desc" ? "descending" : "none"
      }
      className={cn("px-3 py-2 font-medium", alignment)}
    >
      <button
        type="button"
        onClick={sort.onToggle}
        /*
         * THE BUTTON'S NAME IS THE COLUMN'S NAME, and the state is left to `aria-sort`.
         *
         * The tempting alternative — "Sort by Amount, ascending" — says the state twice to anyone
         * who reads both, and says it in a language this primitive would then have to be handed. The
         * WAI-ARIA pattern for a sortable table is exactly this split, and it is also what keeps
         * `@gladis/ui` free of a catalogue dependency for a control that carries no words of its own.
         */
        className={cn(
          "group inline-flex items-center gap-1 rounded-control px-1 -mx-1",
          "hover:text-ink focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          numeric ? "flex-row-reverse" : "flex-row",
        )}
      >
        <span>{children}</span>
        <SortGlyph direction={sort.direction} />
      </button>
    </th>
  );
}

/**
 * Which way this column is sorted, or that it can be.
 *
 * THREE STATES AND NOT TWO. An unsorted sortable column draws a faint pair of carets, so the
 * affordance exists before the first click — a header that looks identical to a plain one until it is
 * clicked is a feature nobody finds. It sharpens on hover through the parent's `group`.
 *
 * `aria-hidden`, correctly: `aria-sort` on the `<th>` already carries this, and naming the glyph would
 * announce the state a second time in whatever words this file happened to choose.
 */
function SortGlyph({ direction }: { direction: "asc" | "desc" | null }): ReactNode {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cn(
        "size-3 shrink-0",
        direction === null ? "text-muted/50 group-hover:text-muted" : "text-ink",
      )}
    >
      {direction === "desc" ? null : <path d="M6 8.5l4-4 4 4" />}
      {direction === "asc" ? null : <path d="M6 11.5l4 4 4-4" />}
    </svg>
  );
}

/**
 * `testId` IS A NAMED PROP RATHER THAN A SPREAD OF REST ATTRIBUTES, and the difference is the bug it
 * closes.
 *
 * This cell accepted no test hook at all, and `AccountList.tsx` had been passing `data-testid` to it
 * since feature 006. The attribute went nowhere: `Cell` spreads nothing, so it was read off the
 * props object and dropped. Nothing failed — TypeScript does not type-check hyphenated JSX attribute
 * names, so an unknown `data-testid` is accepted on any component, and no end-to-end test had yet
 * tried to use that locator to discover it matched nothing.
 *
 * Spreading `...rest` would have made the call site work while leaving the hole open for the next
 * misspelled attribute. A named prop means a typo is a compile error, which is the property the
 * hyphenated form can never have.
 */
function Cell({
  children,
  numeric = false,
  testId,
}: {
  children: ReactNode;
  numeric?: boolean;
  /** For the e2e suite. Lands as `data-testid` on the `<td>`. */
  testId?: string;
}): ReactNode {
  return (
    <td
      {...(testId === undefined ? {} : { "data-testid": testId })}
      className={cn("px-3 py-2", numeric ? "text-right tabular-nums" : "text-left")}
    >
      {children}
    </td>
  );
}

export const Table = Object.assign(TableRoot, { Head, Body, Row, HeaderCell, Cell });
